import { useState, type FormEvent } from 'react';
import {
  useInvitations,
  useCreateInvitation,
  useRevokeInvitation,
} from '../api/queries/invitations';
import { useRbacRoles } from '../api/queries/settings';
import { useClients } from '../api/queries/clients';
import { ApiError } from '../api/client';
import { can } from '../auth/store';
import { fmtDateTime } from '../utils/date';
import { SortableTh } from '../components/SortableTh';
import { TableScroll } from '../components/TableScroll';
import { useClientSort } from '../hooks/useClientSort';
import './InvitationsPage.css';
import { Toggle } from '../components/Toggle';

type InviteSortKey = 'email' | 'roles' | 'client' | 'expires' | 'status' | 'sent_by';

export function InvitationsPage(): JSX.Element {
  if (!can('settings.manage_users')) {
    return <p className="iv-status">You don't have permission to manage invitations.</p>;
  }

  return <InvitationsPageInner />;
}

function InvitationsPageInner(): JSX.Element {
  const list = useInvitations();
  const create = useCreateInvitation();
  const revoke = useRevokeInvitation();
  const rolesQ = useRbacRoles();
  const clientsQ = useClients();

  const [showNew, setShowNew] = useState(false);
  const [email, setEmail] = useState('');
  const [roles, setRoles] = useState<string[]>(['examiner']);
  const [clientId, setClientId] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  const [tokenForCopy, setTokenForCopy] = useState<string | null>(null);

  const onCreate = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (roles.length === 0) {
      setError('Pick at least one role.');
      return;
    }
    try {
      const resp = await create.mutateAsync({
        email: email.trim().toLowerCase(),
        role_names: roles,
        client_id: clientId > 0 ? clientId : undefined,
      });
      setTokenForCopy(resp.invite_token);
      setEmail('');
      setRoles(['examiner']);
      setClientId(0);
      setShowNew(false);
    } catch (err) {
      if (err instanceof ApiError && err.status === 400) setError('Validation failed.');
      else setError('Could not send invitation.');
    }
  };

  const allRoles = (rolesQ.data?.roles ?? []).map((r) => r.name);
  const clients = clientsQ.data?.clients ?? [];
  const invs = list.data?.invitations ?? [];
  const sort = useClientSort(invs, (i, key: InviteSortKey) => {
    switch (key) {
      case 'email': return i.email;
      case 'roles': return i.role_names.join(', ');
      case 'client': return i.client_name ?? '';
      case 'expires': return i.expires_at;
      case 'status': {
        if (i.consumed_at) return 2;
        if (i.expires_at < Date.now()) return 1;
        return 0;
      }
      case 'sent_by': return i.invited_by_name ?? '';
      default: return '';
    }
  }, { key: 'expires', dir: 'desc' });

  return (
    <section className="page-stack page-stack-wide">
      <div className="card iv-toolbar">
      <header className="iv-header">
        <h2>Invitations</h2>
        {!showNew && (
          <button type="button" className="btn-cyan-primary" onClick={() => setShowNew(true)}>
            New invitation
          </button>
        )}
      </header>
      </div>

      {tokenForCopy && (
        <div className="card">
        <div className="iv-token-banner">
          <p><strong>Invitation sent.</strong> If your SMTP is in stub mode, copy this token to share manually:</p>
          <div className="iv-token-row">
            <code>{tokenForCopy}</code>
            <button type="button" className="btn-charcoal-secondary"
              onClick={() => { navigator.clipboard?.writeText(tokenForCopy); }}>
              Copy
            </button>
            <button type="button" className="btn-text" onClick={() => setTokenForCopy(null)}>Dismiss</button>
          </div>
          <p className="iv-token-hint">
            Acceptance URL: <code>/invite/{tokenForCopy}</code>
          </p>
        </div>
        </div>
      )}

      {showNew && (
        <div className="card">
        <form className="iv-form" onSubmit={onCreate}>
          <div className="iv-form-row">
            <label className="iv-field">
              <span>Email</span>
              <input type="email" className="form-input" value={email}
                onChange={(e) => setEmail(e.target.value)} required />
            </label>
            <label className="iv-field">
              <span>Client (optional, scopes portal access)</span>
              <select className="form-input" value={clientId}
                onChange={(e) => setClientId(Number(e.target.value))}>
                <option value={0}>—</option>
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="iv-field">
            <span>Roles</span>
            <div className="iv-roles">
              {allRoles.map((rn) => (
                <Toggle
                  key={rn}
                  checked={roles.includes(rn)}
                  onChange={(v) => {
                      if (v) setRoles([...roles, rn]);
                      else setRoles(roles.filter((r) => r !== rn));
                    }}
                  label={rn}
                />
              ))}
            </div>
          </div>
          {error && <p className="iv-error" role="alert">{error}</p>}
          <div className="iv-form-actions">
            <button type="button" className="btn-charcoal-secondary"
              onClick={() => { setShowNew(false); setError(null); }}>Cancel</button>
            <button type="submit" className="btn-cyan-primary" disabled={create.isPending}>
              {create.isPending ? 'Sending…' : 'Send invitation'}
            </button>
          </div>
        </form>
        </div>
      )}

      {list.isPending ? (
        <div className="card"><p className="iv-status">Loading invitations…</p></div>
      ) : list.error ? (
        <div className="card"><p className="iv-status iv-error">Could not load.</p></div>
      ) : (
        <div className="card card-tight iv-table-card table-scroll-card">
        <TableScroll id="invitations">
        <table className="iv-table">
          <thead>
            <tr>
              <SortableTh column="email" label="Email" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} />
              <SortableTh column="roles" label="Roles" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} />
              <SortableTh column="client" label="Client" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} />
              <SortableTh column="expires" label="Expires" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} />
              <SortableTh column="status" label="Status" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} />
              <SortableTh column="sent_by" label="Sent by" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} />
              <th></th>
            </tr>
          </thead>
          <tbody>
            {sort.sorted.map((i) => {
              const expired = i.consumed_at === null && i.expires_at < Date.now();
              const status = i.consumed_at
                ? <span className="iv-pill iv-pill-ok">used</span>
                : expired
                  ? <span className="iv-pill iv-pill-warn">expired</span>
                  : <span className="iv-pill">pending</span>;
              return (
                <tr key={i.id}>
                  <td>{i.email}</td>
                  <td>{i.role_names.map((r) => <span key={r} className="iv-chip">{r}</span>)}</td>
                  <td>{i.client_name ?? '—'}</td>
                  <td>{fmtDateTime(i.expires_at)}</td>
                  <td>{status}</td>
                  <td>{i.invited_by_name ?? '—'}</td>
                  <td>
                    {i.consumed_at === null && !expired && (
                      <button type="button" className="iv-link"
                        onClick={() => {
                          if (confirm(`Revoke invitation for ${i.email}?`)) {
                            revoke.mutate(i.id);
                          }
                        }}
                        disabled={revoke.isPending}>
                        revoke
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
              </TableScroll>
        </div>
      )}
    </section>
  );
}
